import { useNavigate, useParams } from '@tanstack/react-router';
import React, { DetailedHTMLProps, FormHTMLAttributes, memo, useCallback, useMemo } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { merge } from '@/components';
import { Button, Flex, Heading, Menu, MenuItem, Popover, Text, Tooltip, TooltipTrigger } from '@/components/core';
import {
  AIModelIcon,
  CheckmarkFilledIcon,
  ChevronIcon,
  CloseIcon,
  ErrorDuotoneIcon,
  OverflowIcon,
  RejectIcon,
  SkipIcon,
} from '@/components/icons';
import { AgentRunStatus, ExportMenu, Field, isMultiLineField, TableField, TableRefField } from '@/components/ui';
import { useNextRunId, useTour } from '@/hooks';
import {
  toAgentId,
  toAgentRunId,
  toValidationTaskId,
  useGetAgent,
  useGetAgentRun,
  useGetValidationTask,
  useUpdateValidationTask,
} from '@/hooks/api';
import { useDocumentStore, useValidationStore } from '@/store';
import { findValidationId, findValidationTaskId, getErrors } from '@/utils';

export type DocumentFormProps = DetailedHTMLProps<FormHTMLAttributes<HTMLFormElement>, HTMLFormElement> & {
  hideFormActions?: boolean;
};

const IDocumentForm = ({ className, hideFormActions, ...rest }: DocumentFormProps) => {
  const form = useDocumentStore((state) => state.form);
  const fieldConfig = useDocumentStore((state) => state.fieldConfig);
  const status = useDocumentStore((state) => state.status);
  const attemptedSubmit = useDocumentStore((state) => state.attemptedSubmit);
  const setAttemptedSubmit = useDocumentStore((state) => state.setAttemptedSubmit);
  const isLoading = useDocumentStore((state) => state.isLoading);
  const isEditing = useDocumentStore((state) => state.isEditing);
  const setIsEditing = useDocumentStore((state) => state.setIsEditing);
  const setFocusedField = useDocumentStore((state) => state.setFocusedField);

  const { agentId, runId } = useParams({ from: '/_auth/details/agents/$agentId/runs/$runId' });
  const { mutateAsync: updateValidationTask } = useUpdateValidationTask();
  const { data: agent } = useGetAgent(toAgentId(agentId));

  const addRecentlyCompleted = useValidationStore((state) => state.addRecentlyCompleted);
  const recentlyCompleted = useValidationStore((state) => state.recentlyCompleted);

  const { currentRunIndex, currentRun, previousRun, nextRun, nextRunToReview, totalRuns } = useNextRunId(
    agentId,
    runId
  );

  const navigate = useNavigate();

  const fields = useMemo(() => {
    return Object.entries(fieldConfig ?? {}).map(([label, field]) => {
      if (field.type === 'table') {
        return <TableRefField key={label} name={label} field={field} />;
      } else if (field.type === 'multi-value') {
        return <TableField key={label} rowId={label} name={label} />;
      } else {
        const isMultiLine = isMultiLineField(field);
        return <Field key={label} rowId={label} name={label} isMultiLine={isMultiLine} />;
      }
    });
  }, [fieldConfig]);

  const goToPreviousRun = useCallback(() => {
    const agentRunId = toAgentRunId(agentId, previousRun?.runId);
    if (agentRunId) {
      navigate({
        to: '/details/agents/$agentId/runs/$runId',
        params: agentRunId,
        search: { showPanel: 'agent-runs' },
      }).catch(console.error);
    }
  }, [agentId, previousRun?.runId, navigate]);

  const goToNextRun = useCallback(() => {
    const agentRunId = toAgentRunId(agentId, nextRun?.runId);

    if (agentRunId) {
      navigate({
        to: '/details/agents/$agentId/runs/$runId',
        params: agentRunId,
        search: { showPanel: 'agent-runs' },
      }).catch(console.error);
    }
  }, [agentId, nextRun?.runId, navigate]);

  const goToNextRunToReview = useCallback(async () => {
    const exclusionList = [...(recentlyCompleted ?? []), { agentId, runId }];
    const nextRun = await nextRunToReview(exclusionList);
    addRecentlyCompleted({ agentId, runId });

    const agentRunId = toAgentRunId(agentId, nextRun?.runId);
    if (nextRun && agentRunId) {
      navigate({
        to: '/details/agents/$agentId/runs/$runId',
        params: agentRunId,
        search: { showPanel: 'agent-runs' },
      }).catch(console.error);
    } else {
      navigate({
        to: '/details/agents/$agentId/empty',
        params: { agentId },
        search: { showPanel: 'agent-runs' },
      });
    }
  }, [recentlyCompleted, agentId, runId, nextRunToReview, addRecentlyCompleted, navigate]);

  const rejectRun = useCallback(async () => {
    const taskId = findValidationTaskId(currentRun);
    const validationId = findValidationId(agent);

    updateValidationTask({
      id: toValidationTaskId(validationId, taskId),
      updates: { output: { statusReason: 'rejected' }, status: 'cancelled' },
    });

    goToNextRunToReview();
  }, [agent, currentRun, goToNextRunToReview, updateValidationTask]);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();

      goToNextRunToReview();
    },
    [form, goToNextRunToReview]
  );

  const doneEditing = useCallback(() => {
    setIsEditing(false);
    setFocusedField(undefined);
  }, [setFocusedField, setIsEditing]);

  const formSubscribe = useCallback(
    ([canSubmit, isSubmitting, fieldMetaBase]: any[]) => {
      let fieldsWithError = 0;
      for (const [_, fieldMeta] of Object.entries(fieldMetaBase)) {
        if (getErrors(fieldMeta).length > 0) ++fieldsWithError;
      }

      return (
        <>
          {attemptedSubmit && fieldsWithError > 0 ? (
            <Flex className="m-2 rounded-sm p-2" variant="error" col>
              <div className="flex gap-1">
                <ErrorDuotoneIcon />
                <Heading size="sm">Auto-validation has failed on {fieldsWithError} fields</Heading>
              </div>
              <Text>You need to fix this before continuing</Text>
            </Flex>
          ) : null}
          <div className="flex w-full items-center gap-2 border-t border-gray-200 bg-white p-3">
            {status === 'ready-for-review' ? (
              <>
                <TooltipTrigger>
                  <Tooltip className="max-w-[16rem]" variant="primary">
                    <Flex col className="gap-0.5 p-1">
                      <Heading size="xs">Confirm that all values are correct</Heading>
                      <Text className="!text-gray-400">
                        Values will be ready to export.
                        <br />
                        Corrections will be used to improve the AI.
                      </Text>
                    </Flex>
                  </Tooltip>
                  {canSubmit ? (
                    <Button className="w-1/2" variant="primary" type="submit" isDisabled={isLoading}>
                      <CheckmarkFilledIcon />
                      <Text>{isSubmitting ? 'Submitting...' : 'Confirm'}</Text>
                    </Button>
                  ) : (
                    <Button
                      className="w-1/2"
                      variant="primary"
                      onPress={() => setAttemptedSubmit(true)}
                      isDisabled={attemptedSubmit}
                    >
                      <CheckmarkFilledIcon />
                      <Text>Confirm</Text>
                    </Button>
                  )}
                </TooltipTrigger>
                <div className="flex items-center">
                  <MenuTrigger>
                    <Button variant="plain">
                      <OverflowIcon />
                    </Button>
                    <Popover placement="top left">
                      <Menu>
                        <MenuItem onAction={goToNextRunToReview}>
                          <SkipIcon />
                          <Text>Skip</Text>
                        </MenuItem>
                        <MenuItem variant="error" onAction={rejectRun}>
                          <RejectIcon />
                          <Text>Reject</Text>
                        </MenuItem>
                      </Menu>
                    </Popover>
                  </MenuTrigger>
                </div>
              </>
            ) : isEditing ? (
              <Button variant="primary" onPress={doneEditing}>
                <Text>Done editing</Text>
              </Button>
            ) : (
              <>
                <AgentRunStatus agentId={agentId} runId={runId} />
                <ExportMenu agentId={agentId} runIds={[runId]}>
                  <Button variant="plain">
                    <OverflowIcon />
                  </Button>
                </ExportMenu>
              </>
            )}
            <div className="grow" />
            <Button onPress={goToPreviousRun}>
              <ChevronIcon rotation="left" />
            </Button>
            <Text className="truncate font-medium">{`${currentRunIndex + 1} of ${totalRuns}`}</Text>
            <Button onPress={goToNextRun}>
              <ChevronIcon rotation="right" />
            </Button>
          </div>
        </>
      );
    },
    [
      agentId,
      attemptedSubmit,
      currentRunIndex,
      doneEditing,
      goToNextRun,
      goToNextRunToReview,
      goToPreviousRun,
      isEditing,
      isLoading,
      rejectRun,
      runId,
      setAttemptedSubmit,
      status,
      totalRuns,
    ]
  );

  const { data: run } = useGetAgentRun(toAgentRunId(agentId, runId));
  const { data: task } = useGetValidationTask(toValidationTaskId(null, findValidationTaskId(run)));

  const isInKnowledgeBase = useMemo(() => {
    const isCompleted = status === 'completed';
    const isRejected = task && task.status !== 'succeeded';
    return isCompleted && !isRejected;
  }, [status, task]);

  const { showAddedToAIKnowledgeTooltip, setShowAddedToAIKnowledgeTooltip } = useTour();

  const dismissAddedToAIKnowledgeTooltip = useCallback(() => {
    setShowAddedToAIKnowledgeTooltip(false);
  }, [setShowAddedToAIKnowledgeTooltip]);

  return (
    <form
      key={runId}
      className={merge('bg-gray-25 flex flex-col border-l border-gray-200', className)}
      onSubmit={onSubmit}
      {...rest}
    >
      <Flex col className="h-full">
        {isInKnowledgeBase && showAddedToAIKnowledgeTooltip ? (
          <div className="bg-primary-25 gap-sm m-2 flex items-center rounded-md border border-gray-200 px-2 py-1">
            <AIModelIcon className="text-primary-400 !size-3" />
            <div className="flex items-center gap-1">
              <Text size="xs" className="text-gray-700">
                Added to AI knowledge.
              </Text>
              <Text size="xs" className="text-gray-500">
                Values are used to improve the AI.
              </Text>
            </div>

            <div className="grow" />

            <div className="flex items-center gap-0">
              <Button variant="plain" onPress={() => window.open('https://docs.cradl.ai/', '_blank')}>
                <Text className="!text-primary-500 !text-xs">Learn</Text>
              </Button>
              <Button variant="plain" onPress={dismissAddedToAIKnowledgeTooltip}>
                <CloseIcon />
              </Button>
            </div>
          </div>
        ) : null}

        <Flex col className="p-sm grow gap-6 overflow-auto">
          {fields}
        </Flex>

        {form && !hideFormActions ? (
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting, state.fieldMetaBase, state]}
            children={formSubscribe}
          />
        ) : null}
      </Flex>
    </form>
  );
};

export const DocumentForm = memo(IDocumentForm) as typeof IDocumentForm;
